import { defineStore } from 'pinia';
import { ref } from 'vue';
import { type Article } from '~/types/article';
import useCachedFetch from '~/composables/useCachedFetch';
import { useArticleProcessor } from '~/composables/useArticleProcessor';
import useUrlReplace from '~/composables/useUrlReplace';
import { isForeignArticle } from '~/utils/publication';
import { type InlineItem } from '~/types/articleInline';
import { type PublicationContext } from '~/types';

export const useArticleStore = defineStore('articlesStore', () => {
  const currentArticle = ref({} as Article);
  const loading = ref(true);
  const articleBodyWithoutInjectElements = ref<InlineItem[]>([]);
  const injectElementTypes = ['ad', 'investorchart', 'ma_frame'];
  const useRequestUrl = useRequestURL();
  const nuxtApp = useNuxtApp();
  async function getArticleContent (id: string) {
    try {
      const { replaceUrl } = useUrlReplace();
      loading.value = true;
      const articleIdWithPTKey = id.split('PT');
      const prefix = usePinia()?.state?.value?.profileLoginStore?.isLoggedIn ? 'a' : 'n';

      const articleApi = import.meta.client && useRuntimeConfig().public.article?.api ? useRuntimeConfig().public.article?.api : useRuntimeConfig().article?.originApi;
      const publicationName = useRuntimeConfig().public.name as string;

      const cacheKey = `${prefix}-${id}`;
      const cachedArticle = useCachedFetch('article');

      const result = await cachedArticle
        .getData(cacheKey, () => $fetch(replaceUrl(articleApi, {
          id,
          shareToken: useRequestUrl.searchParams.get('shareToken') || '',
          paywall_debug: useRequestUrl.searchParams.get('paywall_debug') || '',
          unlockToken: useRequestUrl.searchParams.get('unlockToken') || ''
        })), articleIdWithPTKey.length > 1
          ? 0
          : useRuntimeConfig().public.article?.ttl);
      if (isForeignArticle(result.response?.publication?.name, publicationName)) {
        await cachedArticle.cleanCache(cacheKey);
        throw createError({ statusCode: 404, statusMessage: 'Article belongs to a different publication', fatal: true });
      }
      loading.value = result.loading;
      articleBodyWithoutInjectElements.value = result.response?.body?.filter(item => !injectElementTypes.includes(item.type));

      nuxtApp.runWithContext(() => useArticleProcessor(
        result.response as Article,
        useRuntimeConfig().public as unknown as PublicationContext
      ).injectInlineItems());
      currentArticle.value = result.response;
      return result.response;
    } catch (e) {
      if ((e as { fatal?: boolean }).fatal) {
        throw e;
      }
      throw createError({ statusCode: 404, statusMessage: 'Article fetch failed: ' + (e as Error).message });
    }
  }

  return {
    currentArticle,
    loading,
    articleBodyWithoutInjectElements,
    getArticleContent
  };
});
